/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { nextFeedRefreshGeneration } from '../../core/internal/feed-refresh-order.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetEventsRow, GetEventRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String, HELPERS } from '../../helpers/index.js';
import { fromBinary } from '@bufbuild/protobuf';
import { decodeRealtimePayload } from '../../devtool/decode-realtime-payload.js';
import { LiveEventEditorialUpdatedSchema, ScheduledEventUpdatedSchema } from '@morfi/proto';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── useGetEvents ────────────────────────────────────────────────────────────

export interface useGetEventsArgs { readonly activeOnly?: string; readonly limit?: number | string; readonly viewportGated?: boolean; }


async function _useGetEventsQueryFn(
  args: useGetEventsArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetEventsRow>> {
  const url = '/v1/events';
  const params = new URLSearchParams();

  if (args.activeOnly !== undefined) params.set('activeOnly', String(args.activeOnly));
  params.set('limit', String(limitOverride ?? Number(args.limit ?? 24)));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetEvents',
    routeTemplate: '/v1/events',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetEventsRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetEventsFactory = Object.assign(
  (args: useGetEventsArgs) => ({
    queryKey: ['events', 'list', { activeOnly: args.activeOnly ?? null, limit: Number(args.limit ?? 24) }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetEventsQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetEvents',
      viewerSensitive: false,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['events', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetEventsArgs, PagedData<GetEventsRow>>;


export const useGetEventsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/events',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetEventsFactory },
  parseInput: (raw: unknown): useGetEventsArgs => raw as useGetEventsArgs,
  parseResponse: (raw: unknown): PagedData<GetEventsRow> => raw as PagedData<GetEventsRow>,
  specName: 'useGetEvents' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: true as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['live-events', 'entity', id] as readonly unknown[],
  entityProjection: {"resource":"live-events"} as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetEventsRow>>;
export const useGetEventsPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 24,
  maxPageSize: 100,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetEventsArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetEventsQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetEventsPerEntitySubjects(_args: useGetEventsArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = ['morfi.scheduled_event.*.updated.v0', 'morfi.scheduled_event.*.editorial_updated.v0'];
  return [...new Set(_subs)];
}

function _useGetEventsRowSubjectTemplates(_args: useGetEventsArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = [];
  return _tmpls;
}

function _useGetEventsApplyEvent(
  prev: PagedData<GetEventsRow> | undefined,
  ev: MorfiEvent<unknown>,
): PagedData<GetEventsRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.


  // No subscription matched — leave cache untouched.
  return prev;
}


function _useGetEventsOnEvent(
  args: useGetEventsArgs,
  queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown, options?: { updatedAt?: number; refreshGeneration?: number }) => void },
): (ev: MorfiEvent<unknown>) => void {
  const p0 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  const p1 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  return (ev: MorfiEvent<unknown>): void => {
    const subject = ev.subject;
    if (typeof subject !== 'string') return;
    // Each prepend branch decodes its own row var with ITS channel's
    // @morfi/proto schema (different literals may carry different
    // messages) — see emitDecodeRaw. No shared decode here.
      // morfi.scheduled_event.${schedule}.updated.v0
      if (/^morfi\.scheduled_event\.[^.]+\.updated\.v0$/.test(subject)) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(ScheduledEventUpdatedSchema, _d)
            : fromBinary(ScheduledEventUpdatedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof ScheduledEventUpdatedSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['scheduledEvent']) ?? ((raw as Record<string, unknown> | undefined)?.['scheduledEvent']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p0.pending.add(_id);
        if (p0.timer === null) {
          p0.timer = setTimeout(() => {
            p0.timer = null;
            const ids = [...p0.pending];
            p0.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetEventsFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/events`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.activeOnly !== undefined) params.set('activeOnly', Array.isArray(args.activeOnly) ? args.activeOnly.join(',') : String(args.activeOnly));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetEventsRow> | undefined;
              const requestedLimit = Number(args.limit ?? 24);
              const loadedCount = cached?.data.length ?? requestedLimit;
              params.set(
                'limit',
                String(Math.max(requestedLimit, Math.min(loadedCount, 100))),
              );
              const refreshGeneration = nextFeedRefreshGeneration();
              const observedAtMs = Date.now();
              const fetched: GetEventsRow[] = [];
              let json: { data?: ReadonlyArray<GetEventsRow> | Record<string, GetEventsRow>; nextCursor?: string | null };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetEvents.refresh',
                routeTemplate: '/v1/events',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetEvents refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetEventsRow> | Record<string, GetEventsRow>; nextCursor?: string | null };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 100)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetEventsRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                } as PagedData<GetEventsRow>;
              }, { updatedAt: observedAtMs, refreshGeneration });
            })();
          }, 1000);
        }
      }
      // morfi.scheduled_event.*.editorial_updated.v0
      if (/^morfi\.scheduled_event\.[^.]+\.editorial_updated\.v0$/.test(subject)) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(LiveEventEditorialUpdatedSchema, _d)
            : fromBinary(LiveEventEditorialUpdatedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof LiveEventEditorialUpdatedSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['eventId']) ?? ((raw as Record<string, unknown> | undefined)?.['eventId']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p1.pending.add(_id);
        if (p1.timer === null) {
          p1.timer = setTimeout(() => {
            p1.timer = null;
            const ids = [...p1.pending];
            p1.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetEventsFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/events`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.activeOnly !== undefined) params.set('activeOnly', Array.isArray(args.activeOnly) ? args.activeOnly.join(',') : String(args.activeOnly));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetEventsRow> | undefined;
              const requestedLimit = Number(args.limit ?? 24);
              const loadedCount = cached?.data.length ?? requestedLimit;
              params.set(
                'limit',
                String(Math.max(requestedLimit, Math.min(loadedCount, 100))),
              );
              const refreshGeneration = nextFeedRefreshGeneration();
              const observedAtMs = Date.now();
              const fetched: GetEventsRow[] = [];
              let json: { data?: ReadonlyArray<GetEventsRow> | Record<string, GetEventsRow>; nextCursor?: string | null };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetEvents.refresh',
                routeTemplate: '/v1/events',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetEvents refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetEventsRow> | Record<string, GetEventsRow>; nextCursor?: string | null };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 100)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetEventsRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                } as PagedData<GetEventsRow>;
              }, { updatedAt: observedAtMs, refreshGeneration });
            })();
          }, 1000);
        }
      }
  };
}
export const useGetEventsRealtime = {
  domain: 'events' as const,
  domainPins: [] as readonly string[],
  rowKey: 'eventId' as const,
  perEntitySubjects: (args: useGetEventsArgs) => _useGetEventsPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetEventsArgs) => _useGetEventsRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetEventsArgs) =>
    (prev: PagedData<GetEventsRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetEventsRow> | undefined =>
      _useGetEventsApplyEvent(prev, ev),
  onEvent: (args: useGetEventsArgs, queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown, options?: { updatedAt?: number; refreshGeneration?: number }) => void }) =>
    _useGetEventsOnEvent(args, queryClient),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetEventsRow>, unknown>;


// ─── useGetEvent ────────────────────────────────────────────────────────────

export interface useGetEventArgs { readonly eventId: string; }


async function _useGetEventQueryFn(
  args: useGetEventArgs,
  ctx: QueryFunctionContext,
): Promise<GetEventRow | null> {
  if (args.eventId === undefined || args.eventId === null || args.eventId === '') return null;
  const url = `/v1/events/${args.eventId}`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetEvent',
    routeTemplate: '/v1/events/{eventId}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetEventRow;
}


const _useGetEventFactory = Object.assign(
  (args: useGetEventArgs) => ({
    queryKey: ['live-events', 'entity', args.eventId] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetEventQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.data === null ||
      typeof query.state.data !== 'object' ||
      !('eventId' in query.state.data) ||
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetEvent',
      viewerSensitive: false,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['live-events', 'entity', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetEventArgs, GetEventRow>;


export const useGetEventSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/events/{eventId}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetEventFactory },
  parseInput: (raw: unknown): useGetEventArgs => raw as useGetEventArgs,
  parseResponse: (raw: unknown): GetEventRow => raw as GetEventRow,
  specName: 'useGetEvent' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: true as const,
  // MOR-859 — the detail hook reads and writes the same canonical entity as
  // every normalized market list. The completeness guard above still forces
  // a detail fetch when this key currently holds only the narrower list DTO.
  canonicalEntity: true as const,
  entityQueryKey: (id: string) => ['live-events', 'entity', id] as readonly unknown[],
} as unknown as HookSpec<JsonSerialisableArgs, GetEventRow>;

function _useGetEventPerEntitySubjects(args: useGetEventArgs): readonly string[] {
  const subs: readonly string[] = [`morfi.scheduled_event.${args.eventId}.updated.v0`, `morfi.scheduled_event.${args.eventId}.editorial_updated.v0`];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetEventReconcileAlwaysSubjects(args: useGetEventArgs): readonly string[] {
  const _subs: readonly string[] = [`morfi.scheduled_event.${args.eventId}.editorial_updated.v0`];
  return _subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}
function _useGetEventApplyEvent(
  prev: GetEventRow | null | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetEventArgs,
): GetEventRow | null | undefined {
  // A null row (e.g. a 401 → anonymous result) has nothing to patch.
  if (prev === undefined || prev === null) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetEventRow = prev;
    // subject "morfi.scheduled_event.${schedule}.updated.v0"
  {
    const _m = subject.match(/^morfi\.scheduled_event\.([^.]+)\.updated\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(ScheduledEventUpdatedSchema, _d)
            : fromBinary(ScheduledEventUpdatedSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof ScheduledEventUpdatedSchema>>;
        next = (HELPERS.applyLiveEventSchedule as unknown as (prev: GetEventRow, raw: ReturnType<typeof fromBinary<typeof ScheduledEventUpdatedSchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetEventRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  // invalidate: subject "morfi.scheduled_event.*.editorial_updated.v0" → drop cache, force refetch
  if (typeof subject === 'string' && subject.match(/^morfi\.scheduled_event\.[^.]+\.editorial_updated\.v0$/)) { return undefined; }
  return next;
}

export const useGetEventRealtime = {
  domain: 'events' as const,
  domainPins: [] as readonly string[],
  rowKey: 'eventId' as const,
  perEntitySubjects: (args: useGetEventArgs) => _useGetEventPerEntitySubjects(args),
  reconcileAlwaysSubjects: (args: useGetEventArgs) => _useGetEventReconcileAlwaysSubjects(args),
  viewportGated: false as const,
  singleton: true as const,
  applyEvent:
    (args: useGetEventArgs) =>
    (prev: GetEventRow | null | undefined, ev: MorfiEvent<unknown>): GetEventRow | null | undefined =>
      _useGetEventApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, GetEventRow, unknown>;
